import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Calendar, Clock, Play, Trash2, Plus, FileText, BarChart3, ToggleLeft, ToggleRight, RefreshCw } from "lucide-react";
import api from "../../lib/api";


const reportTypes = [
  { value: "completion", label: "Course Completions", icon: "check" },
  { value: "enrollment", label: "Enrollments", icon: "users" },
  { value: "compliance", label: "Compliance", icon: "shield" },
  { value: "assessment", label: "Assessments", icon: "clipboard" },
  { value: "user_activity", label: "User Activity", icon: "activity" },
  { value: "revenue", label: "Revenue", icon: "dollar" },
];

const freqColors = { daily: "#C7322A", weekly: "#1B4F8A", monthly: "#0E8A55" };

const ScheduledReportsPage = () => {
  const { token } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [running, setRunning] = useState(null);
  const [form, setForm] = useState({ name: "", report_type: "completion", frequency: "weekly", format: "csv", recipients: "" });

  const fetchData = useCallback(async () => {
    try {
      const [schedRes, runsRes] = await Promise.all([
        api.get("/report-schedules"),
        api.get("/report-runs"),
      ]);
      setSchedules(schedRes.data.schedules || []);
      setRuns(runsRes.data.runs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createSchedule = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      const recipients = form.recipients.split(",").map(e => e.trim()).filter(Boolean);
      await api.post("/report-schedules", { ...form, recipients });
      toast.success("Report schedule created");
      setShowCreate(false);
      setForm({ name: "", report_type: "completion", frequency: "weekly", format: "csv", recipients: "" });
      fetchData();
    } catch (e) { toast.error("Failed to create"); }
  };

  const runNow = async (id) => {
    setRunning(id);
    try {
      const res = await api.post("/report-schedules/${id}/run-now", {});
      toast.success(`Report generated: ${res.data.row_count} rows`);
      fetchData();
    } catch (e) { toast.error("Failed to run"); }
    setRunning(null);
  };

  const toggleActive = async (sched) => {
    try {
      await api.patch("/report-schedules/${sched.id}", { is_active: !sched.is_active });
      toast.success(sched.is_active ? "Schedule paused" : "Schedule activated");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteSched = async (id) => {
    try {
      await api.delete("/report-schedules/${id}");
      toast.success("Schedule deleted");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Scheduled Reports">
    <div className="p-6 space-y-6" data-testid="scheduled-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Scheduled Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Automate report generation with configurable schedules</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-schedule-btn">
          <Plus className="w-4 h-4 mr-2" /> New Schedule
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Schedules", value: schedules.length, color: "#1B4F8A" },
          { label: "Active", value: schedules.filter(s => s.is_active).length, color: "#0E8A55" },
          { label: "Paused", value: schedules.filter(s => !s.is_active).length, color: "#D97706" },
          { label: "Total Runs", value: runs.length, color: "#7C3AED" },
        ].map((s, i) => (
          <div key={i} className="bg-background border rounded-xl p-4" style={{ borderTop: `3px solid ${s.color}` }}>
            <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">{s.label}</p>
            <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="create-schedule-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Create Report Schedule</h2>
            <div className="space-y-3">
              <div><label className="text-sm font-medium">Schedule Name</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Weekly Completions Report" className="mt-1" data-testid="schedule-name" /></div>
              <div><label className="text-sm font-medium">Report Type</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.report_type} onChange={e => setForm({...form, report_type: e.target.value})} data-testid="report-type-select">
                  {reportTypes.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                </select></div>
              <div><label className="text-sm font-medium">Frequency</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select></div>
              <div><label className="text-sm font-medium">Format</label>
                <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.format} onChange={e => setForm({...form, format: e.target.value})}>
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                </select></div>
              <div><label className="text-sm font-medium">Recipients (comma-separated emails)</label>
                <Input value={form.recipients} onChange={e => setForm({...form, recipients: e.target.value})} placeholder="admin@company.com, manager@company.com" className="mt-1" /></div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={createSchedule} data-testid="submit-schedule-btn">Create Schedule</Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedules */}
      <div className="space-y-3">
        {schedules.length === 0 ? (
          <div className="border rounded-xl p-8 text-center text-muted-foreground">
            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="font-medium">No report schedules yet</p>
            <p className="text-sm mt-1">Create a schedule to automate report generation</p>
          </div>
        ) : schedules.map(sched => (
          <div key={sched.id} className="border rounded-xl p-4 bg-background shadow-sm" data-testid={`schedule-${sched.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sched.is_active ? 'bg-blue-100 text-blue-800' : 'bg-muted text-muted-foreground'}`}>
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">{sched.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground capitalize">{reportTypes.find(r => r.value === sched.report_type)?.label || sched.report_type}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: `${freqColors[sched.frequency] || '#666'}15`, color: freqColors[sched.frequency] || '#666' }}>{sched.frequency}</span>
                    <span className="text-[10px] text-muted-foreground uppercase">{sched.format}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Runs: {sched.run_count || 0}</span>
                <button onClick={() => toggleActive(sched)} className="p-1" data-testid={`toggle-schedule-${sched.id}`}>
                  {sched.is_active ? <ToggleRight className="w-6 h-6 text-green-600" /> : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                </button>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => runNow(sched.id)} disabled={running === sched.id} data-testid={`run-schedule-${sched.id}`}>
                  {running === sched.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-red-600" onClick={() => deleteSched(sched.id)} data-testid={`delete-schedule-${sched.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {sched.last_run && <p className="text-xs text-muted-foreground mt-2">Last run: {new Date(sched.last_run).toLocaleString()}</p>}
            {sched.next_run && <p className="text-xs text-muted-foreground">Next run: {new Date(sched.next_run).toLocaleString()}</p>}
          </div>
        ))}
      </div>

      {/* Recent Runs */}
      {runs.length > 0 && (
        <div className="border rounded-xl overflow-hidden shadow-sm">
          <div className="p-3 bg-muted/50"><h3 className="text-sm font-bold">Recent Report Runs</h3></div>
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/20">
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Type</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Rows</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Format</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Status</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Generated</th>
            </tr></thead>
            <tbody>
              {runs.slice(0, 10).map(r => (
                <tr key={r.id} className="border-t">
                  <td className="p-3 capitalize">{r.report_type?.replace("_", " ")}</td>
                  <td className="p-3">{r.row_count}</td>
                  <td className="p-3 uppercase text-xs">{r.format}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase bg-green-100 text-green-800">{r.status}</span></td>
                  <td className="p-3 text-muted-foreground text-xs">{new Date(r.generated_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default ScheduledReportsPage;
